import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import BatteryChargingFullRoundedIcon from '@mui/icons-material/BatteryChargingFullRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import heroDesktop from './images/z9gt/hero-desktop.jpg';
import heroMobile from './images/z9gt/hero-mobile.jpg';
import colorGreen from './images/z9gt/color-green.png';
import colorBlack from './images/z9gt/color-black.png';
import colorWhite from './images/z9gt/color-white.png';
import colorBlue from './images/z9gt/color-blue.png';
import logoDark from './images/z9gt/logo-dark.png';
import highlightFront from './images/z9gt/highlight-front.jpg';
import highlightHero from './images/z9gt/highlight-hero.jpg';
import highlightBack from './images/z9gt/highlight-back.jpg';
import highlightRear from './images/z9gt/highlight-rear.jpg';
import highlightSide from './images/z9gt/highlight-side.jpg';
import flashCharging from './images/z9gt/flash-charging.jpg';
import pricePoster from './images/z9gt/z9gt-harga.jpg';
import PageSeo, {
  breadcrumbJsonLd,
  keywordSets,
  productJsonLd,
} from './PageSeo';
import ModelPriceSummary from './ModelPriceSummary';
import CarColorShowcase from './CarColorShowcase';
import { createWhatsAppUrl } from './siteConfig';
import { vehiclePrices } from './vehiclePrices';

const PRICE = vehiclePrices.z9gt[0];

// Nama dan gradasi swatch mengikuti data resmi danza.co.id.
const exteriorColors = [
  { label: 'Fjord Green', start: '#6FC3A5', end: '#11AD91', image: colorGreen },
  { label: 'Obsidian Black', start: '#9AA1AA', end: '#030303', image: colorBlack },
  { label: 'Snow White', start: '#FBFAF9', end: '#CDD1D4', image: colorWhite },
  { label: 'Porcelain Blue', start: '#354B78', end: '#1E294A', image: colorBlue },
];

const showcaseStats = [
  { label: 'Jarak tempuh NEDC', value: 701, unit: 'km' },
  { label: 'Tenaga maksimum', value: 1156, unit: 'PS' },
  { label: '0-100 km/jam', value: 2.7, unit: 'detik', decimals: 1 },
];

const interiorOptions = [
  {
    label: 'Perg Beige with Dark Chocolate',
    swatch: 'linear-gradient(135deg, #e6d5bb 0%, #4a2f23 100%)',
  },
  {
    label: 'Full Black Suede with Yellow Stitching',
    swatch: 'linear-gradient(135deg, #2a2a2a 0%, #0c0c0c 70%, #d9b63a 100%)',
  },
  {
    label: 'Red Velvet with Dark Chocolate',
    swatch: 'linear-gradient(135deg, #8d1f2d 0%, #3b221c 100%)',
  },
];

const exteriorHighlights = [
  {
    image: highlightFront,
    title: 'Dual Line Front Light',
    desc: 'Lampu depan dua garis yang tajam menjadi identitas wajah Z9 GT, dipadukan logo Danza di tengah.',
  },
  {
    image: highlightSide,
    title: 'Z-Shape Waistline',
    desc: 'Garis pinggang berbentuk Z menjadi signature design Z9 GT. Proporsi roda terhadap sumbu 1:3 dan rasio bahu bodi di atas 1/2 memberi karakter GT yang panjang, sporty, dan kokoh.',
  },
  {
    image: highlightHero,
    title: 'Velg 20 Inci Multi-Spoke',
    desc: 'Velg alloy multi-spoke 20 inci dengan ban 255/45 R20, tampil sporty sekaligus menopang handling yang stabil.',
  },
  {
    image: highlightBack,
    title: 'Dual Tail Wing',
    desc: 'Spoiler atas berongga melayang mengalirkan udara dari atap, spoiler bawah aktif naik-turun otomatis untuk stabilitas di kecepatan tinggi.',
  },
  {
    image: highlightRear,
    title: 'Time Hourglass Taillight',
    desc: 'Lampu belakang bermotif jam pasir yang menyala penuh, selebar bodi, mudah dikenali di malam hari.',
  },
];

const cabinFeatures = [
  {
    title: 'Tiga layar 2.5K dan AR-HUD 50 inci',
    desc: 'Panel instrumen 13,2 inci, head unit 17,3 inci, dan layar co-pilot 13,2 inci, semuanya 2.5K dengan prosesor 3 nm. Ditambah AR-HUD proyeksi 50 inci dan layar AC 6 inci untuk penumpang belakang.',
  },
  {
    title: 'Kursi depan 8 arah + lumbar 4 arah',
    desc: 'Headrest dan leg-rest elektrik, pemanas, ventilasi, pijat, dan memori. Side bolster kursi pengemudi menopang tubuh secara aktif saat menikung.',
  },
  {
    title: 'Baris belakang elektrik dan Boss Key',
    desc: 'Sandaran dan leg rest belakang elektrik, Boss Key untuk mengatur kursi depan dari belakang, armrest dengan wireless charging 50 W, dan kaca rias lipat berlampu.',
  },
  {
    title: 'Devialet 20 speaker',
    desc: 'Audio high-end asal Prancis dengan 20 speaker, terintegrasi dengan ambient light multi-warna untuk suasana kabin yang imersif.',
  },
  {
    title: 'Smart Refrigerator 4 liter',
    desc: 'Pendingin -6 sampai 6 °C dan penghangat 35 sampai 50 °C, muat 6 kaleng, bisa diaktifkan dari aplikasi.',
  },
  {
    title: 'Material premium dan kaca lapis ganda',
    desc: 'Atap knitted velvet, kulit asli, ornamen kayu alami. Kaca double layer berlapis silver menurunkan suhu kabin hingga 7 °C, sunroof panoramik dengan tirai ganda menahan 99,99% UV. AC 4 zona.',
  },
  {
    title: 'Smart Door dan Projection Tailgate',
    desc: 'Empat pintu elektrik soft-close dengan 4 radar pendeteksi rintangan. Bagasi terbuka dengan menginjak proyeksi logo, suspensi belakang turun otomatis saat memuat barang.',
  },
  {
    title: 'Digital Rearview Mirror 9,1 inci',
    desc: 'Pandangan 3 kali lebih luas dari spion biasa, resolusi 1920x384, auto-dimming, tetap jernih di malam hari.',
  },
  {
    title: 'Google built-in dan Smart Key',
    desc: 'Google Maps, Google Assistant, dan Google Play terintegrasi. Akses lewat key fob, kartu NFC, Bluetooth, Danza App, dan Digital Key. Voice assistant 4 zona multi-bahasa.',
  },
];

const platformFeatures = [
  {
    title: 'Triple-Motor Drive',
    desc: 'Satu motor AC asynchronous di depan (230 kW / 410 Nm) dan dua motor PMSM independen di belakang (total 620 kW / 800 Nm). Tombol BOOST melepas torsi 1.210 Nm secara instan hingga 20 detik.',
  },
  {
    title: 'Rear Wheel Steering independen',
    desc: 'Radius putar hanya 4,62 m, paling kecil di kelasnya. Mode Crab Walk menggerakkan keempat roda searah hingga 15°, dan Pencil Compass Turn mengunci satu roda depan untuk putar balik di jalan sempit.',
  },
  {
    title: 'Vehicle Motion Control (VMC)',
    desc: 'Mengoordinasikan kemudi, rem, suspensi, dan tenaga secara terintegrasi. Teruji moose test 93,6 km/jam dan tetap stabil saat pecah ban di 180 km/jam.',
  },
  {
    title: 'DiSus-A dual chamber air suspension',
    desc: 'Suspensi udara dua ruang mengatur ketinggian 100 sampai 145 mm dan kekakuan secara otomatis. Lengan depan double-wishbone dan belakang 5 multi-link dari aluminium.',
  },
];

const indonesiaNotes = [
  {
    title: 'LiDAR belum aktif',
    desc: 'Sensor LiDAR di bagian depan sudah terpasang, tetapi fitur mengemudi otonomnya menunggu regulasi di Indonesia sebelum diaktifkan.',
  },
  {
    title: 'Sensor pembuka pintu tidak tersedia',
    desc: 'Unit Indonesia tidak dilengkapi sensor buka pintu otomatis. Pintu tetap elektrik soft-close dengan tombol di door trim dan pilar A.',
  },
  {
    title: 'Parkir otomatis tetap perlu pengawasan',
    desc: 'Automated Parking Assist membantu proses parkir, namun masih berupa driver assistance: pengemudi tetap memantau dan memegang kendali.',
  },
];

const specGroups = [
  {
    title: 'Dimensi',
    rows: [
      ['Panjang x Lebar x Tinggi', '5.180 x 1.990 x 1.490 mm'],
      ['Wheelbase', '3.125 mm'],
      ['Radius putar minimum', '4,62 m'],
      ['Bagasi depan (frunk)', '53 L'],
      ['Bagasi belakang (kursi tegak / dilipat)', '495 / 1.680 L'],
      ['Velg dan ban', '20 inci multi-spoke, 255/45 R20'],
    ],
  },
  {
    title: 'Powertrain & Performa',
    rows: [
      ['Baterai', 'Blade Battery 2.0 (LFP) + CTB, arsitektur 1000 V'],
      ['Kapasitas baterai', '122,49 kWh'],
      ['Pengisian AC maksimum', '11 kW'],
      ['Pengisian DC', 'BYD Flash Charging: 10-70% 5 menit, 10-97% 9 menit'],
      ['Konfigurasi motor', 'Three-Motor (1 depan + 2 belakang independen), AWD'],
      ['Tenaga sistem maksimum', '1.156 PS / 850 kW'],
      ['Torsi sistem maksimum', '1.210 Nm (BOOST hingga 20 detik)'],
      ['Akselerasi 0-100 km/jam', '2,7 detik'],
      ['Kecepatan maksimum', '240 km/jam'],
      ['Jarak tempuh (NEDC)', '701 km'],
      ['V2L (Vehicle-to-Load)', 'Hingga 4 kW'],
    ],
  },
  {
    title: 'Sasis & Keselamatan',
    rows: [
      ['Suspensi depan / belakang', 'Double-wishbone aluminium / 5 multi-link aluminium'],
      ['Sistem suspensi', 'DiSus-A dual chamber air suspension, ketinggian 100-145 mm'],
      ['Kemudi roda belakang', 'Independen, Crab Walk hingga 15°, Pencil Compass Turn'],
      ['Airbag', '9 (depan, tengah depan, samping depan dan belakang, tirai)'],
      ['Struktur bodi', '91,5% baja kekuatan tinggi (1180 dan 1500 MPa) dan aluminium alloy'],
      ['Uji stabilitas', 'Moose test 93,6 km/jam, kontrol pecah ban hingga 180 km/jam'],
      ['Mode berkendara', 'Snow, Race, Drift, Custom (e³ Platform)'],
    ],
  },
];

const heroStats = [
  { icon: BoltRoundedIcon, value: '1.156 PS', label: 'Three-Motor AWD' },
  { icon: SpeedRoundedIcon, value: '2,7 detik', label: '0-100 km/jam' },
  { icon: RouteRoundedIcon, value: '701 km', label: 'Jarak tempuh NEDC' },
  { icon: BatteryChargingFullRoundedIcon, value: '9 menit', label: '10-97% Flash Charging' },
];

function JelajahiZ9GT() {
  const isMobile = useMediaQuery('(max-width:768px)');

  return (
    <>
      <PageSeo
        title='Danza Z9 GT BSD Serpong | Harga Rp2,25 Miliar, Spesifikasi, Booking'
        description='Jelajahi Danza Z9 GT di BYD BSD Serpong. Shooting brake listrik e³ Platform three-motor AWD 1.156 PS, 0-100 km/jam 2,7 detik, rear wheel steering radius putar 4,62 m, DiSus-A, Blade Battery 2.0 dengan Flash Charging. Harga Rp2.250.000.000 OTR Jakarta.'
        keywords={[
          ...keywordSets.z9gt,
          ...keywordSets.danza,
          ...keywordSets.localDealer,
          'shooting brake listrik',
          'BYD Flash Charging',
          'Danza Z9 GT 1156 PS',
          'Danza Z9 GT three motor AWD',
        ]}
        path='/jelajahi-z9gt'
        image={heroDesktop}
        jsonLd={[
          productJsonLd({
            name: 'Danza Z9 GT',
            alternateName: 'Denza Z9 GT',
            description:
              'Shooting brake listrik premium Danza Z9 GT dengan three-motor AWD 1.156 PS, Blade Battery Generasi 2, dan BYD Flash Charging.',
            image: heroDesktop,
            path: '/jelajahi-z9gt',
            price: PRICE.priceValue,
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Danza Z9 GT', path: '/jelajahi-z9gt' },
          ]),
        ]}
      />

      <Box component='main' sx={{ width: '100%', overflow: 'hidden' }}>
        <HeroSection isMobile={isMobile} />

        <CarColorShowcase
          title='Danza Z9 GT'
          logo={logoDark}
          kicker='Exterior'
          description='Eksterior Danza Z9 GT terinspirasi dari keanggunan sutra yang mengalir: garis bersih yang menyeimbangkan kehalusan, kekuatan, dan efisiensi aerodinamika.'
          colors={exteriorColors}
          defaultColor='Fjord Green'
          imageRatio={55.3}
          stats={showcaseStats}
          cta={{ label: 'Lihat Spesifikasi', href: '#spesifikasi-z9gt' }}
        />

        <FlashChargingSection />

        <PlatformSection />

        <HighlightSection />

        <CabinSection />

        <SpecSection />

        <PriceSection />

        <IndonesiaNotesSection />

        <CtaSection />
      </Box>
    </>
  );
}

function HeroSection({ isMobile }) {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: '94vh', md: '100vh' },
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden',
        backgroundColor: '#0b0f14',
      }}
    >
      <Box
        component='img'
        src={isMobile ? heroMobile : heroDesktop}
        alt='Danza Z9 GT shooting brake listrik warna Fjord Green'
        sx={{
          position: 'absolute',
          top: 0, right: 0, bottom: 0, left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: { xs: 'center 60%', md: 'center' },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0, right: 0, bottom: 0, left: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.05) 40%, rgba(0,0,0,0.72) 100%)',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          pb: { xs: 5, md: 7 },
          color: '#fff',
        }}
      >
        <Typography
          sx={{
            display: 'inline-block',
            px: 1.4,
            py: 0.5,
            borderRadius: '999px',
            backgroundColor: 'rgba(255,255,255,0.14)',
            border: '1px solid rgba(255,255,255,0.28)',
            fontSize: '0.78rem',
            fontWeight: 800,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
          }}
        >
          Baru · Technology Drives Elegance
        </Typography>
        <Typography
          component='h1'
          sx={{
            mt: 1.5,
            fontSize: { xs: '2.6rem', sm: '3.6rem', md: '5rem' },
            fontWeight: 900,
            lineHeight: 0.98,
          }}
        >
          Danza Z9 GT
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 680,
            fontSize: { xs: '1rem', md: '1.2rem' },
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.9)',
          }}
        >
          Shooting brake listrik three-motor AWD dengan Blade Battery Generasi 2
          dan BYD Flash Charging. {PRICE.price} OTR Jakarta.
        </Typography>

        <Box
          sx={{
            mt: { xs: 3, md: 4 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 1,
            maxWidth: 900,
          }}
        >
          {heroStats.map(({ icon: Icon, value, label }) => (
            <Box
              key={label}
              sx={{
                border: '1px solid rgba(255,255,255,0.26)',
                backgroundColor: 'rgba(0,0,0,0.4)',
                backdropFilter: 'blur(6px)',
                px: 2,
                py: 1.5,
                borderRadius: '10px',
              }}
            >
              <Icon sx={{ fontSize: 22, color: '#8fd3c8' }} />
              <Typography sx={{ mt: 0.5, fontSize: '1.35rem', fontWeight: 800 }}>
                {value}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', opacity: 0.82 }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function FlashChargingSection() {
  return (
    <Box
      sx={{
        backgroundColor: '#061418',
        color: '#fff',
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
          gap: { xs: 3, md: 5 },
          alignItems: 'center',
        }}
      >
        <Box
          component='img'
          src={flashCharging}
          alt='BYD Flash Charging: ready in 5, full in 9 minutes, 10 flash charger pertama di dealer Danza'
          loading='lazy'
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: '16px',
            display: 'block',
            boxShadow: '0 30px 60px rgba(0,0,0,0.45)',
          }}
        />
        <Box>
          <Eyebrow light>Blade Battery Gen 2.0 + BYD Flash Charging</Eyebrow>
          <Typography
            component='h2'
            sx={{
              mt: 1,
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              lineHeight: 1.05,
            }}
          >
            Siap jalan dalam 5 menit, penuh dalam 9 menit
          </Typography>
          <Typography
            sx={{ mt: 2, lineHeight: 1.75, color: 'rgba(255,255,255,0.82)' }}
          >
            Blade Battery 2.0 berkapasitas 122,49 kWh dengan arsitektur 1000 V
            menyatu langsung dengan bodi (Cell-to-Body). Densitas energinya 5%
            lebih tinggi dari generasi pertama dan lolos uji tusuk paku tanpa
            terbakar, bahkan saat flash charging. Dengan BYD Flash Charger,
            10-70% tercapai sekitar 5 menit dan 10-97% sekitar 9 menit. Sepuluh
            Flash Charger pertama di Indonesia tersedia di dealer Danza.
          </Typography>
          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1.5,
            }}
          >
            {[
              ['5 menit', '10% ke 70%'],
              ['9 menit', '10% ke 97%'],
              ['10 lokasi', 'Flash Charger pertama'],
            ].map(([value, label]) => (
              <Box
                key={label}
                sx={{
                  p: 1.5,
                  borderRadius: '10px',
                  border: '1px solid rgba(143,211,200,0.3)',
                  backgroundColor: 'rgba(143,211,200,0.08)',
                }}
              >
                <Typography sx={{ fontSize: '1.3rem', fontWeight: 900, color: '#8fd3c8' }}>
                  {value}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', opacity: 0.8 }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function PlatformSection() {
  const stats = [
    ['4,62 m', 'Radius putar'],
    ['15°', 'Crab Walk'],
    ['1.210 Nm', 'BOOST instan'],
    ['4 kW', 'V2L'],
  ];

  return (
    <Box
      id='platform-e3'
      sx={{ backgroundColor: '#0f1115', color: '#fff', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}
    >
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow light>e³ Platform</Eyebrow>
        <SectionTitle light>Tiga motor, kemudi roda belakang, satu otak</SectionTitle>
        <Typography sx={{ mt: 1.5, maxWidth: 780, color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
          Platform e³ memadukan tiga motor listrik, kemudi roda belakang
          independen, dan Vehicle Motion Control. Hasilnya tenaga kelas supercar
          yang tetap lincah di jalan sempit dan stabil dalam kondisi darurat.
        </Typography>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 1.5,
          }}
        >
          {stats.map(([value, label]) => (
            <Box
              key={label}
              sx={{
                p: 2,
                borderRadius: '12px',
                border: '1px solid rgba(143,211,200,0.3)',
                backgroundColor: 'rgba(143,211,200,0.06)',
              }}
            >
              <Typography sx={{ fontSize: { xs: '1.5rem', md: '1.9rem' }, fontWeight: 900, color: '#8fd3c8' }}>
                {value}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', opacity: 0.8 }}>{label}</Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
          }}
        >
          {platformFeatures.map((item) => (
            <Box
              key={item.title}
              sx={{
                p: 2.5,
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.12)',
                backgroundColor: 'rgba(255,255,255,0.05)',
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: '1.1rem' }}>{item.title}</Typography>
              <Typography sx={{ mt: 0.8, lineHeight: 1.65, color: 'rgba(255,255,255,0.78)' }}>
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function HighlightSection() {
  return (
    <Box sx={{ backgroundColor: '#fff', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Exterior Design</Eyebrow>
        <SectionTitle>The Fluidity of Silk</SectionTitle>
        <Typography sx={{ mt: 1.5, maxWidth: 760, color: '#4b5563', lineHeight: 1.7 }}>
          Terinspirasi aliran kain sutra: garis bodi mulus, permukaan terpahat,
          dan proporsi shooting brake yang tidak dimiliki sedan listrik lain di
          kelasnya.
        </Typography>
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
            gap: 2.5,
          }}
        >
          {exteriorHighlights.map((item) => (
            <Box
              key={item.title}
              sx={{
                borderRadius: '14px',
                overflow: 'hidden',
                border: '1px solid rgba(17,24,39,0.08)',
                backgroundColor: '#fff',
                boxShadow: '0 12px 26px rgba(16,24,40,0.06)',
              }}
            >
              <Box
                component='img'
                src={item.image}
                alt={`Danza Z9 GT ${item.title}`}
                loading='lazy'
                sx={{ width: '100%', height: 'auto', display: 'block' }}
              />
              <Box sx={{ p: 2.2 }}>
                <Typography sx={{ fontWeight: 900, fontSize: '1.1rem' }}>
                  {item.title}
                </Typography>
                <Typography sx={{ mt: 0.6, color: '#4b5563', lineHeight: 1.6 }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CabinSection() {
  return (
    <Box sx={{ backgroundColor: '#111827', color: '#fff', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow light>Interior</Eyebrow>
        <SectionTitle light>Kabin kelas flagship</SectionTitle>
        <Box sx={{ mt: 2.5, display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 3 } }}>
          {interiorOptions.map((option) => (
            <Box key={option.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: option.swatch,
                  border: '1px solid rgba(255,255,255,0.3)',
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.85)' }}>
                {option.label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {cabinFeatures.map((item) => (
            <Box
              key={item.title}
              sx={{
                p: 2.5,
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.12)',
                backgroundColor: 'rgba(255,255,255,0.05)',
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: '1.05rem' }}>
                {item.title}
              </Typography>
              <Typography sx={{ mt: 0.8, lineHeight: 1.6, color: 'rgba(255,255,255,0.78)' }}>
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function SpecSection() {
  return (
    <Box
      id='spesifikasi-z9gt'
      sx={{
        backgroundColor: '#f4f6f8',
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        scrollMarginTop: 96,
      }}
    >
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Spesifikasi</Eyebrow>
        <SectionTitle>Danza Z9 GT Standard (AWD)</SectionTitle>
        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2.5,
          }}
        >
          {specGroups.map((group) => (
            <Box
              key={group.title}
              component='table'
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid rgba(17,24,39,0.08)',
                '& caption': {
                  textAlign: 'left',
                  px: 2,
                  py: 1.5,
                  fontWeight: 900,
                  color: '#fff',
                  backgroundColor: '#1f2937',
                },
                '& td': {
                  px: 2,
                  py: 1.2,
                  borderTop: '1px solid rgba(17,24,39,0.08)',
                  fontSize: '0.95rem',
                  verticalAlign: 'top',
                },
                '& td:first-of-type': { color: '#4b5563', width: '52%' },
                '& td:last-of-type': { fontWeight: 700, color: '#111827' },
              }}
            >
              <caption>{group.title}</caption>
              <tbody>
                {group.rows.map(([label, value]) => (
                  <tr key={label}>
                    <td>{label}</td>
                    <td>{value}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function PriceSection() {
  return (
    <Box sx={{ backgroundColor: '#fff' }}>
      <Box
        sx={{
          maxWidth: 1180,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          pt: { xs: 6, md: 8 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '0.8fr 1.2fr' },
          gap: { xs: 3, md: 5 },
          alignItems: 'center',
        }}
      >
        <Box
          component='img'
          src={pricePoster}
          alt='Harga Danza Z9 GT IDR 2.250.000.000 OTR Jakarta'
          loading='lazy'
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: '16px',
            display: 'block',
            boxShadow: '0 20px 44px rgba(16,24,40,0.16)',
          }}
        />
        <Box>
          <Eyebrow>Harga resmi</Eyebrow>
          <SectionTitle>{PRICE.price}</SectionTitle>
          <Typography sx={{ mt: 1, color: '#4b5563', lineHeight: 1.7 }}>
            OTR Jakarta untuk Danza Z9 GT Standard three-motor AWD. Hubungi
            Junius untuk booking unit, pilihan warna, dan simulasi pembiayaan.
          </Typography>
        </Box>
      </Box>
      <ModelPriceSummary model='Danza Z9 GT' priceKey='z9gt' />
    </Box>
  );
}

function IndonesiaNotesSection() {
  return (
    <Box sx={{ backgroundColor: '#fff', px: { xs: 2, md: 6 }, py: { xs: 6, md: 8 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Transparan sejak awal</Eyebrow>
        <SectionTitle>Catatan untuk unit Indonesia</SectionTitle>
        <Typography sx={{ mt: 1.5, maxWidth: 760, color: '#4b5563', lineHeight: 1.7 }}>
          Spesifikasi tiap negara disesuaikan dengan regulasi setempat. Tiga hal
          ini sering ditanyakan, jadi kami sampaikan di depan.
        </Typography>
        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {indonesiaNotes.map((item) => (
            <Box
              key={item.title}
              sx={{
                p: 2.5,
                borderRadius: '12px',
                backgroundColor: '#f8f9fb',
                border: '1px solid rgba(17,24,39,0.1)',
              }}
            >
              <Typography sx={{ fontWeight: 900, color: '#111827' }}>{item.title}</Typography>
              <Typography sx={{ mt: 0.8, color: '#4b5563', lineHeight: 1.65, fontSize: '0.95rem' }}>
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CtaSection() {
  return (
    <Box sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 }, backgroundColor: '#f4f6f8' }}>
      <Box sx={{ maxWidth: 980, mx: 'auto', textAlign: 'center' }}>
        <Typography
          component='h2'
          sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 900, color: '#111827' }}
        >
          Tertarik dengan Danza Z9 GT?
        </Typography>
        <Typography sx={{ mt: 2, mx: 'auto', maxWidth: 780, color: '#4b5563', lineHeight: 1.7 }}>
          Hubungi Junius di BYD BSD Serpong untuk booking Danza Z9 GT, cek
          ketersediaan warna, promo, dan jadwal test drive.
        </Typography>
        <Button
          href={createWhatsAppUrl(
            `Halo Junius, saya tertarik dengan Danza Z9 GT (${PRICE.price}). Mohon info booking, warna, dan promonya.`,
          )}
          target='_blank'
          rel='noopener noreferrer'
          variant='contained'
          sx={{
            mt: 3,
            px: 4,
            py: 1.25,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 800,
            backgroundColor: '#1f5fd1',
            '&:hover': { backgroundColor: '#184ca8' },
          }}
        >
          Tanya Harga Danza Z9 GT
        </Button>
      </Box>
    </Box>
  );
}

function Eyebrow({ children, light = false }) {
  return (
    <Typography
      sx={{
        color: light ? 'rgba(255,255,255,0.72)' : '#4c5867',
        fontWeight: 800,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        fontSize: '0.82rem',
      }}
    >
      {children}
    </Typography>
  );
}

function SectionTitle({ children, light = false }) {
  return (
    <Typography
      component='h2'
      sx={{
        mt: 1,
        fontSize: { xs: '2rem', md: '3rem' },
        fontWeight: 900,
        color: light ? '#fff' : '#111827',
        lineHeight: 1.05,
      }}
    >
      {children}
    </Typography>
  );
}

export default JelajahiZ9GT;
